import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X } from 'lucide-react';
import type { HeaderFilter, HeaderFilterOption } from '../../../contexts/DynamicHeaderContext';
import { GradientCheckbox } from './GradientCheckbox';

interface MultiCheckboxFilterProps {
  filter: HeaderFilter;
}

export function MultiCheckboxFilter({ filter }: MultiCheckboxFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedValues = Array.isArray(filter.value) ? filter.value : [];
  const options = filter.options || [];

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 256;
      const dropdownHeight = 500;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top = rect.bottom + 4;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        top = rect.top - dropdownHeight - 4;
      }

      const spaceRight = window.innerWidth - rect.left;
      let left = rect.left;
      if (spaceRight < dropdownWidth + 16) {
        left = rect.right - dropdownWidth;
      }
      left = Math.max(8, Math.min(left, window.innerWidth - dropdownWidth - 8));

      setPosition({ top: Math.max(8, top), left });
    }
  };

  const handleToggle = () => {
    if (!isOpen) updatePosition();
    setIsOpen(!isOpen);
    setSearchQuery('');
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const toggleValue = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    filter.onChange(newValues);
  };

  const selectAll = () => filter.onChange(options.map(o => o.value));
  const clearAll = () => filter.onChange([]);

  const filteredOptions = filter.searchable && searchQuery
    ? options.filter(o => o.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : options;

  const getButtonLabel = () => {
    if (selectedValues.length === 0) return filter.label;
    if (selectedValues.length === 1) {
      const opt = options.find(o => o.value === selectedValues[0]);
      return opt?.label || selectedValues[0];
    }
    if (selectedValues.length === options.length) return `${filter.label}: Tutti`;
    return `${filter.label}: ${selectedValues.length}`;
  };

  const hasSelection = selectedValues.length > 0;

  const gradientTextStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #E91E8C, #F97316)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`inline-flex items-center gap-1.5 px-2.5 h-7 text-xs rounded-md focus:outline-none transition-colors ${
          hasSelection
            ? 'bg-[rgba(233,30,140,0.08)] hover:bg-[rgba(233,30,140,0.13)]'
            : 'text-foreground hover:bg-black/5 dark:hover:bg-white/5'
        }`}
      >
        <span className="truncate max-w-[100px]" style={hasSelection ? gradientTextStyle : {}}>
          {getButtonLabel()}
        </span>
        <ChevronDown
          className={`w-3 h-3 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${hasSelection ? 'text-[#E91E8C]' : 'text-muted-foreground'}`}
        />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-64 max-h-[500px] rounded-lg shadow-xl bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-white/10 z-[20050] flex flex-col"
          style={{ top: position.top, left: position.left }}
        >
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            {filter.searchable && (
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca..."
                  className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#E91E8C]"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            {!filter.hideSelectAll && (
              <div className="flex items-center justify-between text-xs">
                <button
                  onClick={selectAll}
                  className="hover:underline"
                  style={gradientTextStyle}
                >
                  Seleziona tutti
                </button>
                <button
                  onClick={clearAll}
                  className="text-muted-foreground hover:underline"
                >
                  Deseleziona tutti
                </button>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-1">
            {filteredOptions.length === 0 ? (
              // Distinto da "nessun risultato": un elenco mai popolato non e'
              // una ricerca fallita, ed e' un'informazione diversa per chi legge.
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                {filter.options?.length ? 'Nessun risultato' : 'Nessuna voce configurata'}
              </div>
            ) : (
              filteredOptions.map(option => {
                const currentValues = Array.isArray(filter.value) ? filter.value : [];
                const isSelected = currentValues.includes(option.value);
                const isIndented = option.label.startsWith('  └') || (option as any).indent;
                const displayLabel = option.label.replace(/^\s*└\s*/, '');

                return (
                  <div
                    key={option.value}
                    onClick={(e) => { e.stopPropagation(); toggleValue(option.value); }}
                    className={`flex items-center gap-2.5 py-2 rounded hover:bg-gray-50 dark:hover:bg-white/10 cursor-pointer ${
                      isIndented ? 'pl-6 pr-2.5' : 'px-2.5'
                    }`}
                  >
                    <GradientCheckbox checked={isSelected} />

                    {option.color && (
                      <span
                        className={`rounded-full flex-shrink-0 ${isIndented ? 'w-2 h-2' : 'w-2.5 h-2.5'}`}
                        style={{ backgroundColor: option.color }}
                      />
                    )}

                    {option.icon && (
                      <span className="flex-shrink-0">{option.icon}</span>
                    )}

                    <span className={`truncate ${
                      isIndented
                        ? 'text-xs text-gray-600 dark:text-gray-300'
                        : 'text-sm font-medium text-gray-700 dark:text-gray-200'
                    }`}>
                      {displayLabel}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
            {selectedValues.length} di {options.length} selezionati
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
